import { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  Typography,
  IconButton,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Paper,
  Divider,
  Grid,
  Card,
  CardContent,
  Tooltip,
  Alert,
  Stack,
  useTheme,
  alpha,
  useMediaQuery,
  TablePagination,
  InputAdornment,
} from "@mui/material";
import { 
  Delete, 
  CloudDownload, 
  Add, 
  Search as SearchIcon, 
  Description as DescriptionIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
  AddCircle as AddCircleIcon,
  FileUpload as FileUploadIcon
} from "@mui/icons-material";
import { DataGrid, GridColDef, GridPaginationModel, GridRenderCellParams } from "@mui/x-data-grid";
import Swal from "sweetalert2";
import {
  getJobDescriptions,
  uploadJobDescription,
  deleteJobDescription,
  downloadJobDescription
} from "../Services/jobDescriptionService";
import { getUser } from "../Services/authService";

// Define the type for job descriptions
interface JobDescription {
  id: number;
  title: string;
  company?: { id: number; name: string };
  createdAt: string;
  filePath: string;
}

const ManageJobDescriptions = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [jobDescriptions, setJobDescriptions] = useState<JobDescription[]>([]);
  const [filteredJDs, setFilteredJDs] = useState<JobDescription[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [title, setTitle] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [totalRows, setTotalRows] = useState(0);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [uploading, setUploading] = useState(false); // New state for upload button loading

  const user = getUser();

  useEffect(() => {
    fetchJobDescriptions();
  }, [page, rowsPerPage]);

  // Apply search filter
  useEffect(() => {
    if (jobDescriptions.length > 0) {
      let result = [...jobDescriptions];
      
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        result = result.filter(
          jd => 
            jd.title.toLowerCase().includes(searchLower) || 
            (jd.company?.name && jd.company.name.toLowerCase().includes(searchLower))
        );
      }
      
      setFilteredJDs(result);
      setPage(0); // Reset to first page when search changes
    }
  }, [jobDescriptions, searchTerm]);

  const fetchJobDescriptions = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getJobDescriptions(user.company.id, page + 1, rowsPerPage);
      setJobDescriptions(data.items);
      setFilteredJDs(data.items);
      setTotalRows(data.totalCount);
    } catch (error) {
      console.error("Failed to fetch job descriptions", error);
      Swal.fire("Error", "Failed to fetch job descriptions", "error");
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, user.company.id]);

  const handleUpload = async () => {
    if (!file || !title) {
      setSubmitError("Please provide both a title and a file");
      return;
    }

    if (uploading) return; // Prevent multiple submissions
    
    setUploading(true); // Set loading state
    setSubmitError(null);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("title", title);
    formData.append("companyId", user.company.id.toString());
    
    try {
      await uploadJobDescription(formData);
      setUploading(false); // Reset loading state
      Swal.fire("Success", "Job Description uploaded successfully", "success");
      fetchJobDescriptions();
      handleCloseDialog();
    } catch (error) {
      console.error("Upload failed", error);
      setSubmitError("Upload failed. Please try again.");
      setUploading(false); // Reset loading state on error too
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setTitle("");
    setFile(null);
    setSubmitError(null);
    setUploading(false); // Reset upload state when closing dialog
  };

  const handleDelete = async (id: number) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to recover this file!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!"
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteJobDescription(id);
          Swal.fire("Deleted!", "Job Description has been removed.", "success");
          fetchJobDescriptions();
        } catch (error) {
          console.error("Failed to delete", error);
          Swal.fire("Error", "Failed to delete", "error");
        }
      }
    });
  };

  // Handle pagination changes
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const renderFilters = () => (
    <Box sx={{ mb: 3, p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 1 }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={8}>
          <TextField
            variant="outlined"
            fullWidth
            placeholder="Search by title"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            size="small"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => setSearchTerm("")}
            size="small"
          >
            Clear Filters
          </Button>
        </Grid>
      </Grid>
    </Box>
  );

  // Render cards for mobile view
  const renderCards = () => (
    <Grid container spacing={2} sx={{ mt: 2 }}>
      {filteredJDs.length === 0 ? (
        <Grid item xs={12}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              No job descriptions found matching your search.
            </Typography>
          </Paper>
        </Grid>
      ) : (
        filteredJDs.map((jd) => (
          <Grid item xs={12} key={jd.id}>
            <Card elevation={2} sx={{ overflow: 'visible' }}>
              <CardContent>
                <Grid container alignItems="center" spacing={1}>
                  <Grid item>
                    <DescriptionIcon color="primary" />
                  </Grid>
                  <Grid item xs>
                    <Typography variant="h6" component="div">
                      {jd.title}
                    </Typography>
                  </Grid>
                </Grid>
                
                <Divider sx={{ my: 1.5 }} />
                
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                  <BusinessIcon fontSize="small" color="action" />
                  <Typography variant="body2">{jd.company?.name || user.company.name}</Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                    <CalendarIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      {new Date(jd.createdAt).toLocaleDateString(undefined, { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}{" "}
                      at{" "}
                      {new Date(jd.createdAt).toLocaleTimeString(undefined, { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}
                    </Typography>
                  </Stack>

                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
                  <Button
                    startIcon={<CloudDownload />}
                    size="small"
                    onClick={() => downloadJobDescription(jd.id)}
                    color="primary"
                  >
                    Download
                  </Button>
                  <Button
                    startIcon={<Delete />}
                    color="error"
                    size="small"
                    onClick={() => handleDelete(jd.id)}
                  >
                    Delete
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))
      )}
    </Grid>
  );

  // Column definitions for DataGrid
  const columns: GridColDef[] = [
    { 
      field: "title", 
      headerName: "Title", 
      flex: 1,
      minWidth: 200,
      renderHeader: () => (
        <Stack direction="row" spacing={1} alignItems="center">
          <DescriptionIcon fontSize="small" />
          <Typography fontWeight="bold">Title</Typography>
        </Stack>
      )
    },
    /* {
      field: "company",
      headerName: "Company",
      flex: 1,
      minWidth: 150,
      renderHeader: () => (
        <Stack direction="row" spacing={1} alignItems="center">
          <BusinessIcon fontSize="small" />
          <Typography fontWeight="bold">Company</Typography>
        </Stack>
      ),
      renderCell: (params: any) => params.row?.company?.name || user.company.name
    }, */
    {
      field: "createdAt",
      headerName: "Created At",
      flex: 1,
      minWidth: 150,
      renderHeader: () => (
        <Stack direction="row" spacing={1} alignItems="center">
          <CalendarIcon fontSize="small" />
          <Typography fontWeight="bold">Last Updated</Typography>
        </Stack>
      ),
      renderCell: (params) => {
        if (!params.value) return "N/A"; // Handle null or undefined values
    
        const formattedDate = new Date(params.value).toLocaleDateString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
    
        const formattedTime = new Date(params.value).toLocaleTimeString(undefined, {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true, // Ensures AM/PM format
        });
    
        return `${formattedDate} at ${formattedTime}`;
      },
    },    
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      renderHeader: () => (
        <Typography fontWeight="bold">Actions</Typography>
      ),
      renderCell: (params: GridRenderCellParams<JobDescription>) => (
        <Box display="flex" alignItems="center">
          <Tooltip title="Download">
            <IconButton 
              color="primary" 
              onClick={() => downloadJobDescription(params.row.id)}
              size="small"
              sx={{ mr: 1 }}
            >
              <CloudDownload />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton 
              color="error" 
              onClick={() => handleDelete(params.row.id)}
              size="small"
            >
              <Delete />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  // Render table view for desktop
  const renderTable = () => (
    <Paper
      elevation={2}
      sx={{ 
        borderRadius: 1, 
        overflow: 'hidden',
        height: 'calc(100vh - 250px)',
        minHeight: 400
      }}
    >
      <DataGrid
        rows={filteredJDs}
        columns={columns}
        paginationModel={{
          page,
          pageSize: rowsPerPage,
        }}
        pageSizeOptions={[5, 10, 25]}
        rowCount={totalRows}
        paginationMode="server"
        onPaginationModelChange={(model) => {
          setPage(model.page);
          setRowsPerPage(model.pageSize);
        }}
        loading={loading}
        disableRowSelectionOnClick
        sx={{
          border: 'none',
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.text.primary,
          },
          '& .MuiDataGrid-cell': {
            borderBottom: `1px solid ${theme.palette.divider}`
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.05)
          }
        }}
      />
    </Paper>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
          Manage Job Descriptions
        </Typography>
        <Button 
          variant="contained" 
          onClick={() => setOpenDialog(true)} 
          color="primary"
          startIcon={<AddCircleIcon />}
          size={isMobile ? "small" : "medium"}
        >
          {isMobile ? "Add" : "Add Job Description"}
        </Button>
      </Box>
      
      <Divider sx={{ mb: 3 }} />
      
      {/* Filter Controls */}
      {!loading && renderFilters()}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {isMobile ? renderCards() : renderTable()}
          
          {/* Mobile Pagination */}
          {isMobile && filteredJDs.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={totalRows}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Box>
          )}
        </>
      )}

      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        fullWidth 
        maxWidth="sm"
        fullScreen={isMobile}
        PaperProps={{
          elevation: 2,
          sx: { borderRadius: 1 }
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: `1px solid ${theme.palette.divider}`,
          py: 2
        }}>
          <Typography variant="h6">
            Add Job Description
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ mt: 2, pt: 2 }}>
          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submitError}
            </Alert>
          )}
          
          <Grid container spacing={2}>
            <Grid item xs={12} paddingTop={2}>
              <TextField
                label="Job Title"
                fullWidth
                required
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                margin="dense"
                InputProps={{ 
                  startAdornment: <DescriptionIcon color="action" sx={{ mr: 1 }} /> 
                }}
                variant="outlined"
                error={!title && submitError !== null}
                helperText={!title && submitError !== null ? "Title is required" : ""}
                disabled={uploading}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
                Company
              </Typography>
              <Box sx={{ 
                p: 2, 
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center'
              }}>
                <BusinessIcon color="action" sx={{ mr: 1 }} />
                <Typography>{user.company.name}</Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} sx={{ mt: 1 }}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
                Upload Job Description
              </Typography>
              <Paper
                elevation={0}
                sx={{
                  border: '2px dashed',
                  borderColor: file ? theme.palette.primary.main : theme.palette.divider,
                  borderRadius: 2,
                  p: 3,
                  backgroundColor: alpha(theme.palette.primary.main, file ? 0.05 : 0),
                  textAlign: 'center',
                  transition: 'all 0.2s ease',
                  cursor: uploading ? 'not-allowed' : 'pointer',
                  '&:hover': {
                    borderColor: uploading ? (file ? theme.palette.primary.main : theme.palette.divider) : theme.palette.primary.main,
                    backgroundColor: uploading ? (file ? alpha(theme.palette.primary.main, 0.05) : 'transparent') : alpha(theme.palette.primary.main, 0.05)
                  }
                }}
              >
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  style={{ display: 'none' }}
                  id="file-upload"
                  disabled={uploading}
                />
                <label htmlFor="file-upload" style={{ 
                  cursor: uploading ? 'not-allowed' : 'pointer', 
                  width: '100%', 
                  display: 'block',
                  opacity: uploading ? 0.7 : 1
                }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <FileUploadIcon fontSize="large" color={file ? "primary" : "action"} />
                    <Typography variant="body1" sx={{ mt: 2, fontWeight: 500 }}>
                      {file ? file.name : 'Click to upload or drag and drop'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                      PDF, DOC, or DOCX (Max 10MB)
                    </Typography>
                  </Box>
                </label>
              </Paper>
              {!file && submitError !== null && (
                <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                  Please select a file
                </Typography>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Button 
            onClick={handleCloseDialog} 
            variant="outlined"
            color="inherit"
            disabled={uploading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleUpload} 
            variant="contained" 
            color="primary"
            disabled={uploading}
            startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {uploading ? "Uploading..." : "Upload"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageJobDescriptions;